/**
 * Deal selection helpers shared by the standard unit listing's price cards and
 * the details modal they open.
 */

import Deal from '@r6digital/rapidstor-types/types/Deal';
import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { getDealDefaultText } from '@/core/utils/dealDisplay';
import { formatDealCustomText } from '@/features/rapidstor/helpers';

interface DealLike {
    _id: string;
}

export interface DealCardCopy {
    /** Text for the card's coloured header, or undefined when the deal has none. */
    label?: string;
    /** Custom lines shown under the price, with the rate tokens substituted. */
    primary?: string;
    secondary?: string;
}

const nonEmpty = (text: string | undefined): string | undefined => {
    const trimmed = text?.trim();
    return trimmed || undefined;
};

/**
 * The copy a price card shows for a deal.
 *
 * The operator's custom format (the Deals tab in the admin panel) wins: its
 * heading replaces the deal title in the header, and its primary and secondary
 * lines appear under the price with `{{DiscountRate}}`-style tokens filled in,
 * the same substitution the journey-based listing applies. Without a custom
 * format the header falls back to the title, then to the default deal text.
 */
export const getDealCardCopy = (deal: Deal, location: Location): DealCardCopy => {
    const { customFormat } = deal;
    const heading = nonEmpty(customFormat?.heading);

    return {
        label: (heading && formatDealCustomText(deal, location, heading))
            || nonEmpty(deal.title)
            || getDealDefaultText(deal, location)
            || undefined,
        primary: nonEmpty(formatDealCustomText(deal, location, nonEmpty(customFormat?.primary))),
        secondary: nonEmpty(formatDealCustomText(deal, location, nonEmpty(customFormat?.secondary))),
    };
};

/**
 * Which deal a unit type should be booked with.
 *
 * Prefers the deal the user picked in the listing's price cards, falling back
 * to the unit's promoted (first) deal. A selection made on another unit type is
 * ignored — the id has to belong to this unit's own deals — so the fallback
 * also covers switching between units.
 *
 * @param deals — the unit type's deals, in promoted-first order
 * @param selectedDealId — the deal the user picked, if any
 * @returns the deal id to book with, or null when the unit has no deals
 */
export const resolveSelectedDealId = (
    deals: DealLike[] | undefined,
    selectedDealId: string | null | undefined,
): string | null => {
    if (selectedDealId && deals?.some((d) => d._id === selectedDealId)) {
        return selectedDealId;
    }

    return deals?.[0]?._id ?? null;
};

export default resolveSelectedDealId;
